import { useState } from "react";
import type { LeadLabelWithUsage, PIPELINE_STAGE_COLORS } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";
import {
  useCreateLabel,
  useDeleteLabel,
  useLabelUsageQuery,
  useUpdateLabel,
} from "@/hooks/useLeadLabels";
import { useCrmStore } from "@/stores/crm.store";

type PaletteColor = (typeof PIPELINE_STAGE_COLORS)[number];

function ColorRow({
  value,
  onPick,
}: {
  value: string | null;
  onPick: (color: PaletteColor) => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      {PIPELINE_COLOR_OPTIONS.map((c) => (
        <button
          key={c.name}
          type="button"
          aria-label={`Color ${c.name}`}
          onClick={() => onPick(c.name)}
          className={`h-5 w-5 rounded-full ${c.swatch} ${
            value === c.name ? "ring-2 ring-ink-900 ring-offset-1" : ""
          }`}
        />
      ))}
    </div>
  );
}

/**
 * Mounted by `LabelsProvider`, not by a page.
 *
 * Every surface that offers "Administrar etiquetas" — the pipeline toolbar and
 * the CardLabels dropdown on the board, the inbox and the lead detail — sits
 * inside that provider. While this lived in `LeadsPipeline` alone, the menu item
 * on /leads/:id and /inbox flipped the store flag with nothing rendering it, so
 * the modal simply never appeared.
 */
export function ManageLabelsModal(): React.JSX.Element {
  const open = useCrmStore((s) => s.isManageLabelsOpen);
  const close = useCrmStore((s) => s.closeManageLabels);
  // Own query key, refetched on open: the delete guard below reads its counts.
  const { data: labels } = useLabelUsageQuery(open);
  const createLabel = useCreateLabel();
  const updateLabel = useUpdateLabel();
  const deleteLabel = useDeleteLabel();

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<PaletteColor>("blue");

  function handleRename(label: LeadLabelWithUsage, value: string): void {
    const name = value.trim();
    if (!name || name === label.name) return;
    updateLabel.mutate({ id: label.id, data: { name } });
  }

  function handleDelete(id: string): void {
    deleteLabel.mutate(id, { onSuccess: () => setConfirmingDeleteId(null) });
  }

  function handleCreate(): void {
    const name = newName.trim();
    if (!name) return;
    createLabel.mutate(
      { name, color: newColor },
      {
        onSuccess: () => {
          setNewName("");
          setNewColor("blue");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Administrar etiquetas</DialogTitle>
          <DialogDescription>
            Crea, renombra y colorea etiquetas. Solo puedes eliminar una etiqueta
            que no esté asignada a ningún prospecto.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 overflow-y-auto pr-1">
          {(labels ?? []).length === 0 && (
            <p className="py-2 text-sm text-ink-400">Aún no hay etiquetas.</p>
          )}
          {(labels ?? []).map((label) => (
            <div
              key={label.id}
              className="rounded-[12px] border border-ink-100 bg-white p-3 flex flex-col gap-2"
            >
              <input
                type="text"
                defaultValue={label.name}
                maxLength={40}
                onBlur={(e) => handleRename(label, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="rounded-[8px] border border-ink-200 px-2 py-1 text-sm text-ink-800 focus:border-moss focus:outline-none"
              />
              <div className="flex flex-wrap items-center gap-3">
                <ColorRow
                  value={label.color}
                  onPick={(color) => updateLabel.mutate({ id: label.id, data: { color } })}
                />
                <div className="ml-auto flex items-center gap-2">
                  {label.assignedCount > 0 ? (
                    <span className="text-xs text-ink-400">
                      Asignada a {label.assignedCount}{" "}
                      {label.assignedCount === 1 ? "prospecto" : "prospectos"}
                    </span>
                  ) : confirmingDeleteId === label.id ? (
                    <>
                      <span className="text-xs text-ink-600">¿Eliminar?</span>
                      <button
                        type="button"
                        disabled={deleteLabel.isPending}
                        onClick={() => handleDelete(label.id)}
                        className="rounded-lg bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-40"
                      >
                        Sí
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDeleteId(null)}
                        className="rounded-lg border border-ink-200 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-050"
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingDeleteId(label.id)}
                      className="rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Inline create — name + color, avoids duplicate blank names. */}
        <div className="mt-1 flex flex-col gap-2 rounded-[12px] border border-dashed border-ink-300 p-3">
          <input
            type="text"
            value={newName}
            maxLength={40}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            placeholder="Nueva etiqueta"
            className="rounded-[8px] border border-ink-200 px-2 py-1 text-sm text-ink-800 focus:border-moss focus:outline-none"
          />
          <div className="flex items-center justify-between gap-2">
            <ColorRow value={newColor} onPick={setNewColor} />
            <button
              type="button"
              disabled={!newName.trim() || createLabel.isPending}
              onClick={handleCreate}
              className="rounded-lg bg-moss px-3 py-1.5 text-sm font-medium text-white hover:bg-moss/90 disabled:opacity-40"
            >
              Agregar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
